/**
 * sceneManager.js - Scene, renderer, camera, environment, and animation loop
 */

import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { HDRLoader } from 'three/examples/jsm/loaders/HDRLoader.js';
import { RoomEnvironment } from 'three/examples/jsm/environments/RoomEnvironment.js';
import * as state from './state.js';

// ===== TEXTURE LOADER =====
export const textureLoader = new THREE.TextureLoader();

// ===== SCENE SETUP =====
export function setupEnvironment() {
    if (state.floor) state.scene.remove(state.floor);
    if (state.grid) state.scene.remove(state.grid);

    const floorGeo = new THREE.PlaneGeometry(10000, 10000);
    const floorMat = new THREE.ShadowMaterial({
        color: 0x000000,
        opacity: 0.25,
        depthWrite: true
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.renderOrder = 1;
    state.scene.add(floor);
    state.setFloor(floor);

    const gridSize = 200;
    const gridDivisions = gridSize;
    const grid = new THREE.GridHelper(gridSize, gridDivisions, 0x000000, 0x000000);
    grid.material.transparent = true;
    grid.material.opacity = 0.2;
    grid.material.depthWrite = false;
    grid.material.depthTest = true;
    grid.material.toneMapped = false;
    grid.renderOrder = 0;
    grid.position.y = floor.position.y + 0.02;
    grid.userData.gridSize = gridSize;
    grid.userData.isInfinite = true;
    state.scene.add(grid);
    state.setGrid(grid);
}

// ===== CAMERA UTILITIES =====
export function storeInitialView() {
    state.initialCamPos.copy(state.camera.position);
    state.initialTarget.copy(state.controls.target);
    state.setInitialCamDist(state.camera.position.distanceTo(state.controls.target));
}

export function applyInitialZoom() {
    if (state.initialZoom === 100 || !state.initialCamDist) return;

    // Calculate target distance: at 70% zoom, camera is further away
    // zoomPercent = (initialCamDist / currentDistance) * 100
    // => currentDistance = (initialCamDist / zoomPercent) * 100
    const targetDistance = (state.initialCamDist / state.initialZoom) * 100;

    // Move camera along the direction from target to camera
    const direction = new THREE.Vector3().subVectors(state.camera.position, state.controls.target).normalize();
    state.camera.position.copy(state.controls.target).addScaledVector(direction, targetDistance);

    state.controls.update();
    requestRender();
}

export function fitCameraToObject(obj) {
    state._tempBox3.setFromObject(obj);
    const size = state._tempBox3.getSize(state._tempVector3);
    const center = state._tempBox3.getCenter(state._tempVector3_2);
    const maxDim = Math.max(size.x, size.y, size.z);
    const fov = THREE.MathUtils.degToRad(state.camera.fov);
    let dist = maxDim / (2 * Math.tan(fov / 2));
    dist *= 0.8;

    // Dreiviertelansicht: Kamera leicht seitlich rotieren (um Y-Achse)
    // 25 Grad Rotation für schöne Dreiviertelansicht
    const angle = THREE.MathUtils.degToRad(25);
    state.camera.position.set(
        center.x + dist * Math.sin(angle),
        center.y,
        center.z + dist * Math.cos(angle)
    );
    state.controls.target.copy(center);
    state.controls.update();
}

export function adjustCameraForDevice() {
    const isMobile = window.innerWidth < 768;
    const baseDist = state.initialCamDist || state.camera.position.distanceTo(state.controls.target);

    state._tempVector3.subVectors(state.camera.position, state.controls.target).normalize();
    const multiplier = isMobile ? 1.4 : 1.0;
    state._tempVector3_2.copy(state.controls.target).addScaledVector(state._tempVector3, baseDist * multiplier);
    state.camera.position.copy(state._tempVector3_2);
    state.camera.updateProjectionMatrix();
}

export function adjustFogToModel(obj) {
    if (!state.scene.fog) return;

    // Calculate bounding box and size
    const bbox = new THREE.Box3().setFromObject(obj);
    const size = bbox.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);

    // Check if mobile/tablet
    const isMobile = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (window.innerWidth <= 767);

    // Scale fog based on model size
    // Mobile gets slightly more range for large models
    const fogNear = isMobile ? maxDim * 2.1 : maxDim * 2;
    const fogFar = isMobile ? maxDim * 5.8 : maxDim * 5.2;

    state.scene.fog.near = fogNear;
    state.scene.fog.far = fogFar;

    // Adjust max zoom distance based on model size
    // Keep it below fogFar so models stay visible
    if (state.controls) {
        const maxDistance = isMobile ? maxDim * 4.5 : maxDim * 4;
        state.controls.maxDistance = maxDistance;
    }
}

export function createFakeShadow(obj) {
    try {
        // Remove old fake shadow if exists
        if (state.fakeShadow) {
            state.scene.remove(state.fakeShadow);
            if (state.fakeShadow.material && state.fakeShadow.material.map) {
                state.fakeShadow.material.map.dispose();
            }
            if (state.fakeShadow.material) {
                state.fakeShadow.material.dispose();
            }
            if (state.fakeShadow.geometry) {
                state.fakeShadow.geometry.dispose();
            }
            state.setFakeShadow(null);
        }

        // Calculate model bounding box
        const bbox = new THREE.Box3().setFromObject(obj);
        const size = bbox.getSize(new THREE.Vector3());
        const center = bbox.getCenter(new THREE.Vector3());

    // Create orthographic camera looking down at the model
    const maxDim = Math.max(size.x, size.z);
    const frustumSize = maxDim * 0.6;
    const shadowCamera = new THREE.OrthographicCamera(
        -frustumSize, frustumSize,
        frustumSize, -frustumSize,
        0.1, size.y + 10
    );
    shadowCamera.position.set(center.x, bbox.max.y + 5, center.z);
    shadowCamera.lookAt(center.x, bbox.min.y, center.z);

    // Create render target for shadow texture
    const renderTarget = new THREE.WebGLRenderTarget(1024, 1024);

    // Create temporary scene with only the model (black silhouette)
    const tempScene = new THREE.Scene();
    tempScene.background = new THREE.Color(0xffffff);

    // Clone the model and make it black (deep clone to avoid material issues)
    const tempModel = obj.clone(true);
    const blackMaterial = new THREE.MeshBasicMaterial({
        color: 0x000000,
        side: THREE.DoubleSide
    });

    let meshCount = 0;
    tempModel.traverse((child) => {
        if (child.isMesh) {
            meshCount++;
            // Dispose old material if it exists
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose && mat.dispose());
                } else {
                    child.material.dispose && child.material.dispose();
                }
            }
            child.material = blackMaterial;
        }
    });

    if (meshCount === 0) {
        blackMaterial.dispose();
        return;
    }

    tempScene.add(tempModel);

    // Render the shadow texture
    const currentRenderTarget = state.renderer.getRenderTarget();
    state.renderer.setRenderTarget(renderTarget);
    state.renderer.render(tempScene, shadowCamera);
    state.renderer.setRenderTarget(currentRenderTarget);

    // Create canvas for blurring
    const canvas = document.createElement('canvas');
    canvas.width = 1024;
    canvas.height = 1024;
    const ctx = canvas.getContext('2d');

    // Read pixels from render target
    const pixels = new Uint8Array(1024 * 1024 * 4);
    state.renderer.readRenderTargetPixels(renderTarget, 0, 0, 1024, 1024, pixels);

    // Create ImageData and draw to canvas (flip Y)
    const imageData = ctx.createImageData(1024, 1024);
    for (let y = 0; y < 1024; y++) {
        for (let x = 0; x < 1024; x++) {
            const i = (y * 1024 + x) * 4;
            const flippedY = 1024 - 1 - y;
            const j = (flippedY * 1024 + x) * 4;
            imageData.data[i] = pixels[j];
            imageData.data[i + 1] = pixels[j + 1];
            imageData.data[i + 2] = pixels[j + 2];
            imageData.data[i + 3] = pixels[j + 3];
        }
    }
    ctx.putImageData(imageData, 0, 0);

    // Apply blur for soft shadow (Safari-compatible method)
    // Use downscale-upscale technique instead of ctx.filter which Safari doesn't support well
    const blurPasses = 8;
    const scaleFactor = 0.0625;
    const tempCanvas = document.createElement('canvas');
    const tempCtx = tempCanvas.getContext('2d');
    tempCanvas.width = Math.floor(1024 * scaleFactor);
    tempCanvas.height = Math.floor(1024 * scaleFactor);

    // Enable image smoothing for blur effect
    tempCtx.imageSmoothingEnabled = true;
    tempCtx.imageSmoothingQuality = 'high';
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Multiple passes of downscale-upscale for stronger blur
    for (let i = 0; i < blurPasses; i++) {
        // Downscale
        tempCtx.drawImage(canvas, 0, 0, tempCanvas.width, tempCanvas.height);
        // Upscale back
        ctx.drawImage(tempCanvas, 0, 0, tempCanvas.width, tempCanvas.height, 0, 0, 1024, 1024);
    }

    // Invert colors (black becomes transparent, white becomes black)
    const finalImageData = ctx.getImageData(0, 0, 1024, 1024);
    for (let i = 0; i < finalImageData.data.length; i += 4) {
        const brightness = finalImageData.data[i];
        const alpha = (255 - brightness) / 255 * 0.5; // Inverted and reduced opacity
        finalImageData.data[i] = 0;
        finalImageData.data[i + 1] = 0;
        finalImageData.data[i + 2] = 0;
        finalImageData.data[i + 3] = alpha * 255;
    }
    ctx.putImageData(finalImageData, 0, 0);

    const shadowTexture = new THREE.CanvasTexture(canvas);

    // Create shadow plane
    const shadowSize = maxDim * 1.2;
    const shadowGeometry = new THREE.PlaneGeometry(shadowSize, shadowSize);
    const shadowMaterial = new THREE.MeshBasicMaterial({
        map: shadowTexture,
        transparent: true,
        opacity: 1.0,
        depthWrite: false,
        depthTest: true,
        blending: THREE.MultiplyBlending,
        premultipliedAlpha: true,
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -1
    });

    const fakeShadow = new THREE.Mesh(shadowGeometry, shadowMaterial);
    fakeShadow.rotation.x = -Math.PI / 2;
    fakeShadow.position.set(center.x, bbox.min.y + 0.05, center.z);
    fakeShadow.renderOrder = 3;

    state.scene.add(fakeShadow);
    state.setFakeShadow(fakeShadow);

    // Cleanup
    renderTarget.dispose();
    blackMaterial.dispose();
    } catch (error) {
        console.error('[createFakeShadow] Error creating shadow:', error);
    }
}

export function updateGrid() {
    if (!state.grid || !state.floor) return;
    if (state.allMeshes.length === 0) return;

    state._tempBox3.makeEmpty();
    let hasMesh = false;

    for (let i = 0; i < state.allMeshes.length; i++) {
        const o = state.allMeshes[i];
        if (!o || !o.geometry || !o.visible) continue;

        try {
            state._tempBox3.expandByObject(o);
            hasMesh = true;
        } catch (err) {
            // ignorieren, manche Meshes haben evtl. keine BoundingBox
        }
    }

    if (!hasMesh) return;

    const minY = state._tempBox3.min.y;
    const floorY = minY - 0.01;

    const epsilon = 0.001;
    if (Math.abs(state.floor.position.y - floorY) > epsilon) {
        state.floor.position.y = floorY;
        state.grid.position.y = floorY + 0.02;
    }

    const center = state._tempBox3.getCenter(state._tempVector3_2);
    if (Math.abs(state.grid.position.x - center.x) > epsilon || Math.abs(state.grid.position.z - center.z) > epsilon) {
        state.grid.position.x = center.x;
        state.grid.position.z = center.z;
        state.floor.position.x = center.x;
        state.floor.position.z = center.z;
    }
}

// ===== ZOOM FUNCTIONS =====
let zoomValueEl = null;

export function setZoomValueEl(el) {
    zoomValueEl = el;
}

export function updateZoomDisplay() {
    if (zoomValueEl) zoomValueEl.textContent = `${state.zoomPercent.toFixed(0)} %`;
}

export function applyZoom(percent) {
    const minZ = state.controls.minDistance;
    const maxZ = state.controls.maxDistance;

    const targetZoom = THREE.MathUtils.clamp(percent, 10, 500);
    state.setZoomPercent(targetZoom);

    const newDist = state.initialCamDist * (100 / targetZoom);
    const dir = new THREE.Vector3();
    dir.subVectors(state.camera.position, state.controls.target).normalize();

    const startPos = state.camera.position.clone();
    const endPos = state.controls.target.clone().addScaledVector(dir, newDist);

    const t0 = performance.now(), dur = 300;
    (function tween() {
        const t = Math.min(1, (performance.now() - t0) / dur);
        const e = t * (2 - t);
        state.camera.position.lerpVectors(startPos, endPos, e);
        state.controls.update();
        if (t < 1) requestAnimationFrame(tween);
    })();

    updateZoomDisplay();
}

export function zoomIn() {
    const newZoom = Math.min(500, state.zoomPercent + 10);
    applyZoom(newZoom);
}

export function zoomOut() {
    const newZoom = Math.max(10, state.zoomPercent - 10);
    applyZoom(newZoom);
}

export function resetView() {
    if (!state.initialCamPos || !state.initialTarget) {
        return;
    }

    const startPos = state.camera.position.clone();
    const startTarget = state.controls.target.clone();
    const endTarget = state.initialTarget.clone();

    // Calculate endPos based on initialZoom
    let endPos = state.initialCamPos.clone();
    if (state.initialZoom !== 100 && state.initialCamDist) {
        const targetDistance = (state.initialCamDist / state.initialZoom) * 100;
        const direction = new THREE.Vector3().subVectors(state.initialCamPos, state.initialTarget).normalize();
        endPos = state.initialTarget.clone().addScaledVector(direction, targetDistance);
    }

    const t0 = performance.now(), dur = 500;
    (function tween() {
        const t = Math.min(1, (performance.now() - t0) / dur);
        const e = t * (2 - t); // ease out quad

        state.camera.position.lerpVectors(startPos, endPos, e);
        state.controls.target.lerpVectors(startTarget, endTarget, e);
        state.controls.update();

        if (t < 1) requestAnimationFrame(tween);
    })();

    // Reset zoom to initial value
    state.setZoomPercent(state.initialZoom);
    updateZoomDisplay();
}

// ===== ANIMATION LOOP =====
let frameCount = 0;

export function animate(updateDecalOverlays) {
    requestAnimationFrame(() => animate(updateDecalOverlays));

    if (state.animationPaused) {
        return;
    }

    const controlsChanged = state.controls.update();

    if (frameCount < 5) {
        frameCount++;
    }

    const cameraMoved = !state.camera.position.equals(state.lastCameraPosition) ||
        !state.camera.quaternion.equals(state.lastCameraQuaternion) ||
        !state.controls.target.equals(state.lastControlsTarget);

    if (cameraMoved || controlsChanged) {
        state.setFramesSinceChange(0);
        state.setNeedsMarkerUpdate(true);
        state.setNeedsRender(true);
        state.lastCameraPosition.copy(state.camera.position);
        state.lastCameraQuaternion.copy(state.camera.quaternion);
        state.lastControlsTarget.copy(state.controls.target);
    } else {
        state.setFramesSinceChange(state.framesSinceChange + 1);

        if (state.needsMarkerUpdate && state.framesSinceChange < state.MAX_IDLE_FRAMES) {
            state.setNeedsMarkerUpdate(false);
        }
    }

    if (state.needsRender || cameraMoved || controlsChanged) {
        if (state.needsRender && !cameraMoved && !controlsChanged) {
        }
        state.renderer.render(state.scene, state.camera);
        state.setNeedsRender(false);

        if (state.needsMarkerUpdate) {
            // updateMarkers will be called from markerManager
            state.setNeedsMarkerUpdate(false);
        }

        // Decal-Overlays aktualisieren (nur wenn Grafik im Bearbeitungsmodus)
        if (updateDecalOverlays && state.currentEditingGraphic && state.currentEditingGraphic.isEditing) {
            updateDecalOverlays();
        }
    }
}

export function requestRender() {
    state.setNeedsRender(true);
    state.setNeedsMarkerUpdate(true);
}

// ===== WINDOW RESIZE =====
export function onWindowResize(containerEl) {
    const rect = containerEl.getBoundingClientRect();
    state.camera.aspect = rect.width / rect.height;
    state.camera.updateProjectionMatrix();
    state.renderer.setSize(rect.width, rect.height);
}

export function adjustRendererSize(containerEl) {
    const rect = containerEl.getBoundingClientRect();
    if (rect.width && rect.height) {
        state.renderer.setSize(rect.width, rect.height, false);
        state.camera.aspect = rect.width / rect.height;
        state.camera.updateProjectionMatrix();
    }
}

// ===== INITIALIZATION =====
export function initScene(containerEl, hdrUrl, manager) {
    containerEl.querySelector('canvas')?.remove();

    // Load custom colors from localStorage
    state.loadCustomColors();

    const scene = new THREE.Scene();
    state.setScene(scene);

    // Get container dimensions for correct aspect ratio
    const rect = containerEl.getBoundingClientRect();
    const camera = new THREE.PerspectiveCamera(30, rect.width / rect.height, 0.1, 2000);
    state.setCamera(camera);

    const renderer = new THREE.WebGLRenderer({
        antialias: true,
        powerPreference: "high-performance",
        stencil: false,
        preserveDrawingBuffer: true
    });
    state.setRenderer(renderer);

    renderer.setSize(rect.width, rect.height);

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.autoUpdate = true;
    renderer.toneMapping = THREE.LinearToneMapping;
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMappingExposure = 1;

    containerEl.appendChild(renderer.domElement);

    // Overlay-Container für Decal-Icons erstellen
    let decalOverlayContainer = containerEl.querySelector('.decal-overlay-container');
    if (!decalOverlayContainer) {
        decalOverlayContainer = document.createElement('div');
        decalOverlayContainer.className = 'decal-overlay-container';
        decalOverlayContainer.style.cssText = 'position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 100;';
        containerEl.appendChild(decalOverlayContainer);
    }

    const controls = new OrbitControls(camera, renderer.domElement);
    state.setControls(controls);

    // Mobile/Tablet: Erkennung über Touch-Support UND/ODER Bildschirmbreite
    const isMobile = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (window.innerWidth <= 767);

    // Auf Mobile: Damping komplett deaktivieren um Springen zu vermeiden
    if (isMobile) {
        controls.enableDamping = false;
        controls.rotateSpeed = 0.8; // Rotation etwas langsamer für bessere Kontrolle
    } else {
        controls.enableDamping = true;
        controls.dampingFactor = 0.2;
    }

    controls.enablePan = false;
    controls.screenSpacePanning = false;
    controls.minDistance = 3;
    controls.maxDistance = 100;
    controls.maxPolarAngle = Math.PI / 2;

    // Zoom: Auf Mobile via Pinch aktiviert, auf Desktop über Wheel-Handler
    if (isMobile) {
        controls.enableZoom = true; // Pinch-to-Zoom auf Mobile
        controls.zoomSpeed = 1.0;
        // Touch-Gesten: Ein Finger = Rotation, Zwei Finger = Zoom
        controls.touches = {
            ONE: THREE.TOUCH.ROTATE,
            TWO: THREE.TOUCH.DOLLY_PAN
        };
    } else {
        controls.enableZoom = false; // Desktop verwendet Custom Wheel-Handler
        // Zusätzlich: Entferne den Wheel-Listener von OrbitControls komplett
        controls.domElement.removeEventListener('wheel', controls._onMouseWheel);
    }

    controls.addEventListener('change', () => {
        state.setNeedsMarkerUpdate(true);
        state.setFramesSinceChange(0);
        dismissNavigationOverlay();

        // Update zoom percentage display (especially for mobile pinch-to-zoom)
        if (state.initialCamDist && state.camera) {
            const currentDistance = state.camera.position.distanceTo(state.controls.target);
            const newZoomPercent = (state.initialCamDist / currentDistance) * 100;
            state.setZoomPercent(newZoomPercent);
            updateZoomDisplay();
        }
    });
    window.addEventListener('resize', () => {
        state.setNeedsMarkerUpdate(true);
        state.setFramesSinceChange(0);
    });

    // Load HDR environment with blur for softer reflections
    new HDRLoader(manager).load(hdrUrl, hdr => {
        const pm = new THREE.PMREMGenerator(renderer);

        // Apply blur by rendering HDR to a smaller texture first
        const blurSize = 128; // Small size = more blur
        const blurTarget = new THREE.WebGLRenderTarget(blurSize, blurSize / 2, {
            type: THREE.HalfFloatType,
            format: THREE.RGBAFormat,
            minFilter: THREE.LinearFilter,
            magFilter: THREE.LinearFilter
        });

        // Create a simple scene to render the HDR onto a plane
        const blurScene = new THREE.Scene();
        const blurCam = new THREE.OrthographicCamera(-1, 1, 0.5, -0.5, 0, 1);
        const blurMat = new THREE.MeshBasicMaterial({ map: hdr });
        const blurPlane = new THREE.Mesh(new THREE.PlaneGeometry(2, 1), blurMat);
        blurScene.add(blurPlane);

        // Render to small target (automatic bilinear filtering = blur)
        renderer.setRenderTarget(blurTarget);
        renderer.render(blurScene, blurCam);
        renderer.setRenderTarget(null);

        // Use blurred texture for environment
        blurTarget.texture.mapping = THREE.EquirectangularReflectionMapping;
        scene.environment = pm.fromEquirectangular(blurTarget.texture).texture;
        scene.background = new THREE.Color(0xdddddd);
        scene.fog = new THREE.Fog(0xdddddd, 50, 100);

        // Cleanup
        pm.dispose();
        hdr.dispose();
        blurMat.dispose();
        blurPlane.geometry.dispose();
        blurTarget.dispose();
    });

    // scene.add(new THREE.AmbientLight(0xffffff, 0.6));

    // const fillLight = new THREE.DirectionalLight(0xffffff, 3.0);
    // fillLight.position.set(5, 10, 5);
    // fillLight.castShadow = false;

    // scene.add(fillLight);
    // state.setMainLight(fillLight);

    // const fillLight2 = new THREE.DirectionalLight(0xffffff, 0.3);
    // fillLight2.position.set(-5, 5, -5);
    // scene.add(fillLight2);

    const shadowGroup = new THREE.Group();
    scene.add(shadowGroup);

    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    scene.background = new THREE.Color(0xdddddd);

    const floorGeo = new THREE.PlaneGeometry(10000, 10000);
    const floorMat = new THREE.ShadowMaterial({
        color: 0x000000,
        opacity: 0.4,
        depthWrite: true
    });
    const floor = new THREE.Mesh(floorGeo, floorMat);
    floor.rotation.x = -Math.PI / 2;
    floor.receiveShadow = true;
    floor.renderOrder = 1;
    scene.add(floor);
    state.setFloor(floor);

    const gridSize = 10000;
    const gridDivisions = gridSize;
    const grid = new THREE.GridHelper(gridSize, gridDivisions, 0x000000, 0x000000);
    grid.material.transparent = true;
    grid.material.opacity = 0.2;
    grid.material.depthWrite = false;
    grid.material.depthTest = true;
    grid.material.toneMapped = false;
    grid.renderOrder = 0;
    grid.userData.gridSize = gridSize;
    grid.userData.isInfinite = true;
    scene.add(grid);
    state.setGrid(grid);
    grid.position.y = floor.position.y + 0.02;

    // Setup navigation overlay and custom controls
    setupNavigationOverlay(containerEl, renderer.domElement);
    setupCustomZoomControls(renderer.domElement, controls, camera);

    return { scene, camera, renderer, controls };
}

// Make requestRender globally available
if (typeof window !== 'undefined') {
    window._threeRequestRender = requestRender;
}

// ===== NAVIGATION OVERLAY =====
let navigationOverlay = null;

function setupNavigationOverlay(containerEl, canvasEl) {
    const GLOBALS = window.GLOBALS || { tc: {} };
    const isMobile = window.innerWidth < 768;

    // Auf Mobile: Kein Overlay - OrbitControls funktionieren normal mit einem Finger
    if (isMobile) {
        return;
    }

    // Create overlay (nur Desktop)
    navigationOverlay = document.createElement('div');
    navigationOverlay.className = 'navigation-overlay';

    const iconPath = '/userdata/files/models/scroll-icon.svg';
    const textContent = GLOBALS.tc.nav_instruction_desktop || 'STRG + Scrollen zum Zoomen';

    navigationOverlay.innerHTML = `
        <div class="navigation-overlay__content">
            <div class="navigation-overlay__icon"><img src="${iconPath}" alt="Navigation Icon" /></div>
            <div class="navigation-overlay__text">${textContent}</div>
        </div>
    `;

    containerEl.appendChild(navigationOverlay);

    // Auto-dismiss after 3 seconds on initial load
    setTimeout(() => {
        dismissNavigationOverlay();
    }, 3000);
}

function dismissNavigationOverlay() {
    if (navigationOverlay && navigationOverlay.style.opacity !== '0') {
        navigationOverlay.style.opacity = '0';
        // Don't remove the overlay, just hide it so we can show it again
        setTimeout(() => {
            if (navigationOverlay) {
                navigationOverlay.style.display = 'none';
            }
        }, 300);
    }
}

let overlayTimeout = null;

function showNavigationOverlay() {
    if (navigationOverlay) {
        // Clear any existing timeout to prevent multiple dismissals
        if (overlayTimeout) {
            clearTimeout(overlayTimeout);
            overlayTimeout = null;
        }

        // Only show if not already visible
        if (navigationOverlay.style.opacity !== '1') {
            navigationOverlay.style.display = 'flex';
            // Force reflow for smooth transition
            navigationOverlay.offsetHeight;
            navigationOverlay.style.opacity = '1';
        }
    }
}

// Export for use in controls event listener
export { dismissNavigationOverlay, showNavigationOverlay };

// ===== CUSTOM ZOOM CONTROLS =====
function setupCustomZoomControls(canvasEl, controls, camera) {
    const isMobile = 'ontouchstart' in window;

    if (!isMobile) {
        // Desktop: CTRL+Scroll to zoom
        const wheelHandler = (event) => {
            const target = event.target;
            const isOverConfigurator = target === canvasEl || canvasEl.contains(target);

            if (!isOverConfigurator) {
                return;
            }

            if (event.ctrlKey || event.metaKey) {
                event.preventDefault();
                event.stopPropagation();

                const delta = event.deltaY;
                const zoomSpeed = 0.001;
                const distance = camera.position.distanceTo(controls.target);
                const newDistance = distance + delta * zoomSpeed * distance;

                const clampedDistance = Math.max(controls.minDistance, Math.min(controls.maxDistance, newDistance));

                const direction = new THREE.Vector3();
                direction.subVectors(camera.position, controls.target).normalize();
                camera.position.copy(controls.target).addScaledVector(direction, clampedDistance);

                controls.update();
                state.setNeedsRender(true);

                if (state.initialCamDist) {
                    const newZoomPercent = (state.initialCamDist / clampedDistance) * 100;
                    state.setZoomPercent(newZoomPercent);
                    updateZoomDisplay();
                }
            } else {
                event.stopPropagation();
                showNavigationOverlay();
            }
        };

        // Register on canvas and container to catch all scrolls
        canvasEl.addEventListener('wheel', wheelHandler, { passive: false, capture: true });

        // Also listen on the viewer container
        const viewerContainer = canvasEl.closest('#viewer') || canvasEl.closest('.threeViewer');
        if (viewerContainer && viewerContainer !== canvasEl) {
            viewerContainer.addEventListener('wheel', wheelHandler, { passive: false, capture: true });
        }
    }
    // Mobile: Keine custom Touch-Listener - OrbitControls handhabt alles nativ
    // Das verhindert Glitching und lässt normales Scrollen zu
}

// Small scroll hint removed - now using full navigation overlay instead
